/**
 * BranchPerformanceTable — Phase R10.3 (2026-07-04)
 *
 * Sortable per-branch dashboard rendered from
 * GET /api/owner/receipt-claims/branch-performance. Clicking a branch
 * name row calls `onBranchClick(branch_id)` which the parent uses to
 * filter the list below. Click column headers to toggle sort.
 */
import { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { formatRupiah } from '@/lib/claimErrorUI';

const COLUMNS = [
  { key: 'branch_name',          label: 'Branch',             numeric: false },
  { key: 'total_claims',         label: 'Total',              numeric: true },
  { key: 'approved',             label: 'Approved',           numeric: true },
  { key: 'rejected',             label: 'Rejected',           numeric: true },
  { key: 'pending',              label: 'Pending',            numeric: true },
  { key: 'potential_duplicates', label: 'Potential Duplicates', numeric: true },
  { key: 'total_receipt_amount', label: 'Receipt Total (Rp)',  numeric: true },
  { key: 'approval_rate',        label: 'Approval Rate',       numeric: true },
];

function approvalRateColor(rate) {
  if (rate >= 0.8) return 'text-emerald-300 bg-emerald-500/15';
  if (rate >= 0.5) return 'text-amber-300  bg-amber-500/15';
  return 'text-red-300 bg-red-500/15';
}

export default function BranchPerformanceTable({
  rows = [], isLoading, isError, onBranchClick,
}) {
  const [sortKey, setSortKey] = useState('total_claims');
  const [sortDesc, setSortDesc] = useState(true);

  const sortedRows = useMemo(() => {
    const enriched = rows.map((r) => ({
      ...r,
      potential_duplicates:
        (r.flag_yellow ?? 0) + (r.flag_orange ?? 0) + (r.flag_red ?? 0),
    }));
    const sorted = [...enriched].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') {
        return sortDesc ? bv.localeCompare(av) : av.localeCompare(bv);
      }
      const an = av ?? 0, bn = bv ?? 0;
      return sortDesc ? bn - an : an - bn;
    });
    // Keep "Unassigned" at the bottom regardless of sort.
    const unassigned = sorted.filter((r) => r.branch_id === null);
    const assigned = sorted.filter((r) => r.branch_id !== null);
    return [...assigned, ...unassigned];
  }, [rows, sortKey, sortDesc]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDesc((v) => !v);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  if (isError) {
    return (
      <div className="p-6 text-sm text-red-300" data-testid="branch-perf-error">
        Could not load branch performance.
      </div>
    );
  }

  if (!isLoading && sortedRows.length === 0) {
    return (
      <div className="p-6 text-sm text-white/50 text-center" data-testid="branch-perf-empty">
        No claims in selected date range.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-5 px-5" data-testid="branch-perf-table-wrap">
      <table className="w-full text-sm" data-testid="branch-perf-table">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-white/50 border-b border-white/5">
            {COLUMNS.map((c) => {
              const isSorted = sortKey === c.key;
              const Icon = isSorted ? (sortDesc ? ChevronDown : ChevronUp) : ChevronsUpDown;
              return (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  data-testid={`branch-perf-th-${c.key}`}
                  className={
                    'py-2 px-3 cursor-pointer select-none ' +
                    (c.numeric ? 'text-right' : 'text-left')
                  }
                >
                  <span className={'inline-flex items-center gap-1 ' + (c.numeric ? 'justify-end w-full' : '')}>
                    {c.label}
                    <Icon size={10} className={isSorted ? 'text-gold' : 'text-white/30'} />
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <tr key={`loading-${i}`} className="border-b border-white/5 animate-pulse">
                  {COLUMNS.map((c) => (
                    <td key={c.key} className="py-3 px-3">
                      <span className="inline-block h-4 w-16 rounded bg-white/5" />
                    </td>
                  ))}
                </tr>
              ))
            : sortedRows.map((r) => (
                <tr
                  key={r.branch_id ?? 'unassigned'}
                  className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`branch-perf-row-${r.branch_id ?? 'unassigned'}`}
                >
                  <td className="py-3 px-3">
                    <button
                      type="button"
                      onClick={() => onBranchClick?.(r.branch_id)}
                      className={
                        'font-medium hover:text-gold transition-colors text-left ' +
                        (r.branch_id === null ? 'text-white/50' : 'text-white')
                      }
                      data-testid={`branch-perf-click-${r.branch_id ?? 'unassigned'}`}
                    >
                      {r.branch_name}
                    </button>
                  </td>
                  <td className="py-3 px-3 text-right text-white/80 font-mono">{r.total_claims}</td>
                  <td className="py-3 px-3 text-right text-emerald-300 font-mono">{r.approved}</td>
                  <td className="py-3 px-3 text-right text-red-300 font-mono">{r.rejected}</td>
                  <td className="py-3 px-3 text-right text-amber-300 font-mono">{r.pending}</td>
                  <td className="py-3 px-3 text-right text-orange-300 font-mono">{r.potential_duplicates}</td>
                  <td className="py-3 px-3 text-right text-gold font-mono">{formatRupiah(r.total_receipt_amount)}</td>
                  <td className="py-3 px-3 text-right">
                    <span className={`inline-flex items-center justify-end px-2 py-0.5 rounded-full text-[11px] font-semibold ${approvalRateColor(r.approval_rate)}`}>
                      {(r.approval_rate * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
}
